import {
  classes,
  icons,
  SelectOption,
  TableActionsHeaderButton,
  TablePageSizeSelector,
} from '@portal/components'
import React, { useEffect, useState } from 'react'

import { useCollectionFilterSettings } from '../../store'
import { buildQueryForCollection } from '../../utils'
import {
  useDQLEditorCollectionsContext,
  useDQLEditorContext,
} from '../dqlEditor/context'

const { ChevronLeft, ChevronRight } = icons

const options: SelectOption[] = [
  { value: '25', label: 'Show 25' },
  { value: '50', label: 'Show 50' },
  { value: '100', label: 'Show 100' },
  { value: '200', label: 'Show 200' },
  { value: '500', label: 'Show 500' },
  { value: '1000', label: 'Show 1000' },
]

export default function CollectionPaginationControls() {
  const { setQuery, activeView, sideBarOpen } = useDQLEditorContext()

  const { collectionCounts, activeCollection, currentPage, setCurrentPage } =
    useDQLEditorCollectionsContext()

  const { documentsLimit, setDocumentsLimit } = useCollectionFilterSettings()

  const [pageCount, setPageCount] = useState<number>(0)

  useEffect(() => {
    // Set the page count to the number of pages needed to display all the documents in the collection
    setPageCount(
      Math.ceil(
        (collectionCounts[activeCollection ?? ''] ?? 0) / documentsLimit,
      ) || 1,
    )
  }, [collectionCounts, activeCollection, documentsLimit])

  const onSetPageSize = (size: number) => {
    // Reset the current page to 1 when the page size changes
    setCurrentPage(1)

    size = Number(size)
    setDocumentsLimit(size)

    setQuery(
      buildQueryForCollection(activeCollection ?? '', {
        limit: size,
      }),
    )
  }

  const onSetPage = (page: number) => {
    setCurrentPage(page)
    setQuery(
      buildQueryForCollection(activeCollection ?? '', {
        limit: documentsLimit,
        offset: (page - 1) * documentsLimit,
      }),
    )
  }

  const startDoc = (currentPage - 1) * documentsLimit + 1
  const endDoc = Math.min(
    currentPage * documentsLimit,
    collectionCounts[activeCollection ?? ''] ?? 0,
  )
  const totalDocs = collectionCounts[activeCollection ?? ''] ?? 0

  return (
    <div
      className={classes(
        'flex h-full flex-row pr-4 transition-transform duration-200 ease-in-out',
        activeView === 'collections' && sideBarOpen && '-translate-x-12',
      )}
    >
      <div className="flex h-full items-center gap-2">
        <TableActionsHeaderButton
          data-testid="paginateBack"
          aria-label="Previous page"
          disabled={currentPage === 1}
          onClick={() => onSetPage(currentPage - 1)}
        >
          <ChevronLeft className="h-4 w-4" />
        </TableActionsHeaderButton>

        <TableActionsHeaderButton
          data-testid="paginateForward"
          aria-label="Next page"
          disabled={currentPage === pageCount}
          onClick={() => onSetPage(currentPage + 1)}
        >
          <ChevronRight className="h-4 w-4" />
        </TableActionsHeaderButton>
      </div>

      <div className="flex h-full items-center">
        <TablePageSizeSelector
          pageSize={documentsLimit}
          setPageSize={onSetPageSize}
          options={options}
          label={`${startDoc}-${endDoc} of ${totalDocs} documents`}
        />
      </div>
    </div>
  )
}
